import { InAppState } from "../base/InAppState";

// Tree-shaken ECharts: only the two chart types and three components this
// screen draws. Importing the barrel ("echarts") instead pulls every chart,
// coordinate system and component in — 1107 KB minified against 522 KB here,
// for two charts.
import * as echarts from "echarts/core";
import { BarChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

import template from "./IndexTemplate.html";
import { _lcs, locale } from "../../i18n";

echarts.use( [ BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer ] );

// Every `label` below is a German source string that doubles as its dictionary
// key: these are module constants, evaluated before I18n exists, so they are
// translated where they are read.

// The regional content kinds, in the order the sidebar lists them. Keys match
// the core-api response (StatsFactory.getPlatformStats → content).
const CONTENT_TYPES = [
    { key: "event",  label: "Veranstaltungen",    icon: "calendar-days" },
    { key: "poi",    label: "Sehenswürdigkeiten", icon: "landmark" },
    { key: "gastro", label: "Gastronomie",        icon: "utensils" },
    { key: "tour",   label: "Touren",             icon: "route" }
];

// Status buckets worth charting. The vocabulary also knows "cancelled" and
// "postponed"; both are event-only and rare, so they ride in "Sonstige"
// rather than adding two near-empty series to every bar.
const STATUS_SERIES = [
    { key: "published", label: "Veröffentlicht", color: "#336B5A" },  /* green-700 */
    { key: "draft",     label: "Entwurf",        color: "#D6C59D" },  /* sand-500  */
    { key: "archived",  label: "Archiviert",     color: "#B7C2C6" }   /* slate-300 */
];

// Donut slice per content kind — the brand's four hues, kept distinguishable
// in the order the slices are drawn.
const MIX_COLORS = [ "#1F4B3F", "#6A8A76", "#C2A461", "#76A0B2" ];

const SYNC_LABELS = {
    never   : "noch nie",
    ok      : "erfolgreich",
    partial : "mit Fehlern",
    error   : "fehlgeschlagen"
};

const SYNC_PILLS = {
    never   : { cls: "pill--neutral", icon: "circle" },
    ok      : { cls: "pill--ok",      icon: "circle-check" },
    partial : { cls: "pill--warn",    icon: "triangle-alert" },
    error   : { cls: "pill--err",     icon: "triangle-alert" }
};

/**
 * IndexState — "Übersicht": what the platform currently holds.
 *
 * Every number comes from GET /admin/stats, which counts in Postgres. The
 * screen deliberately shows no per-tenant detail: this is the one view that is
 * about all tenants at once, and the drill-downs already have their own
 * screens — each widget links to the list it summarises.
 *
 * The charts are ECharts instances, not markup. They own their canvas and must
 * be told when the container resizes and disposed on state exit, or the
 * canvas and its animation loop outlive the screen (see exit).
 */
export class IndexState extends InAppState
{
    static ROUTE = '/';
    static ID = 'index';

    async enter()
    {
        // Render into the layout's content slot (set by View.switchToInApp).
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.bodyEl = root.querySelector( '[data-id="body"]' );
        this.msgEl = root.querySelector( '[data-id="msg"]' );
        this.srcListEl = root.querySelector( '[data-id="srclist"]' );

        this.charts = [];
        this.stats = null;

        this.addEventListener( root, "click", ( e ) => this.onAction( e ) );

        // ECharts sizes itself from the container, which changes when the
        // sidebar collapses or the window resizes — neither of which the
        // library observes on its own.
        this._onResize = () => this.charts.forEach( ( chart ) => chart.resize() );
        this.addEventListener( window, "resize", this._onResize );

        await this.loadStats();
    }

    /**
     * Charts hold a canvas and an internal resize/animation loop; letting the
     * state's DOM be replaced without disposing them leaks both.
     */
    async exit()
    {
        this.disposeCharts();
        await super.exit();
    }

    disposeCharts()
    {
        ( this.charts || [] ).forEach( ( chart ) => chart.dispose() );
        this.charts = [];
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadStats()
    {
        this.showMessage( "layout-dashboard", _lcs( "Lade Kennzahlen …" ), _lcs( "Einen Moment bitte." ) );

        try
        {
            const { result } = await this.app.api.get( "/admin/stats" );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected /admin/stats response." );
            }
            this.stats = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Kennzahlen konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.showBody();
        this.renderHero();
        this.renderKpis();
        this.renderAccounts();
        this.renderSources();
        this.renderAiCosts();
        this.renderCharts();
    }

    /* ------------------------------ Rendering ------------------------------- */

    renderHero()
    {
        const content = this.stats.content || {};
        const total = CONTENT_TYPES.reduce( ( sum, type ) => sum + this._total( content[ type.key ] ), 0 );
        const accommodations = ( this.stats.accommodations || {} ).total || 0;

        // Two parameters, on purpose: `count` is the raw number I18n picks the
        // plural form with, `n` is the same number formatted for the locale
        // (thousands separator) and is what the text actually shows.
        this._text(
            "hero-headline",
            _lcs( "{count} Unterkünfte live.", { count: accommodations, n: this._n( accommodations ) } )
        );
        this._text(
            "hero-sub",
            _lcs( "{content} regionale Inhalte aus {sources} Datenquellen stehen allen Gästemappen zur Verfügung.", {
                content : this._n( total ),
                sources : this._n( ( this.stats.sources || {} ).total || 0 )
            } )
        );
    }

    renderKpis()
    {
        const accounts = this.stats.accounts || {};
        const accommodations = this.stats.accommodations || {};
        const sources = this.stats.sources || {};

        this._text( "kpi-clients", this._n( accounts.clients ) );
        this._sub( "kpi-clients-sub", _lcs( "{n} aktiv", { n: this._n( accounts.clientsActive ) } ) );

        this._text( "kpi-accommodations", this._n( accommodations.total ) );
        this._sub( "kpi-accommodations-sub", _lcs( "{n} veröffentlicht", { n: this._n( accommodations.published ) } ) );

        this._text( "kpi-guests", this._n( accounts.guests ) );
        this._sub( "kpi-guests-sub", _lcs( "{n} aktiv", { n: this._n( accounts.guestsActive ) } ) );

        this._text( "kpi-sources", this._n( sources.total ) );

        // A failing feed is the one thing on this screen an operator would act
        // on, so it outranks the "x aktiv" line rather than sitting beside it.
        const failed = sources.failed || 0;
        if ( failed > 0 )
        {
            this._sub(
                "kpi-sources-sub",
                _lcs( "{count} Quellen fehlgeschlagen", { count: failed, n: this._n( failed ) } ),
                "kpi__delta--down",
                "triangle-alert"
            );
        }
        else
        {
            this._sub(
                "kpi-sources-sub",
                _lcs( "{n} aktiv", { n: this._n( sources.enabled ) } ),
                "kpi__delta--up",
                "plug-zap"
            );
        }
    }

    renderAccounts()
    {
        const accounts = this.stats.accounts || {};
        const accommodations = this.stats.accommodations || {};

        const total = accommodations.total || 0;
        const published = accommodations.published || 0;
        const share = total > 0 ? Math.round( published / total * 100 ) : 0;

        this._text( "acct-published", share + " %" );
        this._text( "acct-published-legend", _lcs( "{n} veröffentlicht", { n: this._n( published ) } ) );
        this._text( "acct-draft-legend", _lcs( "{n} Entwurf", { n: this._n( accommodations.draft ) } ) );

        const bar = this.app.view.pageContent.querySelector( '[data-id="acct-published-bar"]' );
        if ( bar ) bar.style.width = share + "%";

        this._text( "acct-clients", this._n( accounts.clients ) );
        this._text( "acct-guests", this._n( accounts.guests ) );
        this._text( "acct-admins", this._n( accounts.admins ) );
    }

    renderSources()
    {
        const sources = this.stats.sources || {};
        const items = Array.isArray( sources.items ) ? sources.items : [];

        if ( items.length === 0 )
        {
            this.srcListEl.innerHTML =
                '<div class="src"><div class="src__body"><p class="src__name">' +
                    this._esc( _lcs( "Keine Datenquellen" ) ) +
                '</p><div class="src__meta">' +
                    this._esc( _lcs( "Noch keine Feeds eingerichtet." ) ) +
                '</div></div></div>';
            return;
        }

        const content = this.stats.content || {};

        this.srcListEl.innerHTML = items.map( ( source ) => {
            const type = CONTENT_TYPES.find( ( t ) => t.key === source.contentType ) ||
                { label: "Inhalte", icon: "layers" };   // label is a dictionary key, see above
            const status = source.isEnabled ? ( source.lastSyncStatus || "never" ) : "never";
            const pill = SYNC_PILLS[ status ] || SYNC_PILLS.never;

            // The feed's own record count is not in this payload — the number
            // shown is the pool its content type feeds, which is what the
            // "Verwalten" link drills into.
            const poolCount = this._total( content[ source.contentType ] );

            return '' +
                '<div class="src">' +
                    '<span class="src__icon"><i class="icon icon--' + type.icon + '"></i></span>' +
                    '<div class="src__body">' +
                        '<p class="src__name">' + this._esc( source.name ) + '</p>' +
                        '<div class="src__meta">' +
                            '<span class="pill ' + ( source.isEnabled ? "pill--region" : "pill--neutral" ) + '">' +
                                this._esc( _lcs( type.label ) ) +
                            '</span>' +
                            '<span class="pill ' + pill.cls + '">' +
                                '<i class="icon icon--' + pill.icon + '"></i> ' +
                                this._esc( SYNC_LABELS[ status ] ? _lcs( SYNC_LABELS[ status ] ) : status ) +
                            '</span>' +
                            '<span class="src__meta-item"><i class="icon icon--clock"></i> ' +
                                this._esc( this._dateText( source.lastSyncAt ) ) +
                            '</span>' +
                        '</div>' +
                    '</div>' +
                    '<span class="src__count">' + this._n( poolCount ) + '</span>' +
                '</div>';
        } ).join( "" );
    }

    /**
     * Vertex AI spend of the last three calendar months (stats.ai, from the
     * aiUsage ledger). One row per month with a bar scaled to the most
     * expensive month, so a spike reads at a glance without a chart.
     */
    renderAiCosts()
    {
        const ai = this.stats.ai || {};
        const months = Array.isArray( ai.months ) ? ai.months : [];
        const currency = ai.currency || "USD";
        const max = months.reduce( ( m, month ) => Math.max( m, month.costUsd || 0 ), 0 );

        this._text( "ai-total", this._money( ai.totalUsd, currency ) );
        this._text( "ai-requests", _lcs( "{count} Anfragen", { count: ai.requests || 0, n: this._n( ai.requests ) } ) );

        const listEl = this.app.view.pageContent.querySelector( '[data-id="ai-months"]' );
        if ( listEl )
        {
            listEl.innerHTML = months.map( ( month ) => {
                const share = max > 0 ? Math.round( ( month.costUsd || 0 ) / max * 100 ) : 0;
                return '' +
                    '<div class="ai-cost__month">' +
                        '<div class="acct__row">' +
                            '<span class="acct__label">' + this._esc( this._monthText( month.month ) ) + '</span>' +
                            '<span class="acct__value">' + this._esc( this._money( month.costUsd, currency ) ) + '</span>' +
                        '</div>' +
                        '<div class="acct__bar"><span style="width: ' + share + '%;"></span></div>' +
                    '</div>';
            } ).join( "" );
        }

        // Calls whose model has no price configured are missing from the
        // totals above. Silence here would make the total look complete.
        const warnEl = this.app.view.pageContent.querySelector( '[data-id="ai-unpriced"]' );
        if ( warnEl )
        {
            const unpriced = ai.unpriced || 0;
            warnEl.hidden = unpriced === 0;
            const textEl = warnEl.querySelector( "span" );
            if ( textEl )
            {
                textEl.textContent = _lcs( "{count} Anfragen ohne hinterlegten Preis", { count: unpriced, n: this._n( unpriced ) } );
            }
        }
    }

    /* -------------------------------- Charts -------------------------------- */

    renderCharts()
    {
        this.disposeCharts();
        this.renderContentChart();
        this.renderMixChart();
    }

    /**
     * Regional content by kind and status — a horizontal stacked bar, because
     * the four category labels are long German words that would need rotating
     * on a vertical axis.
     */
    renderContentChart()
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="chart-content"]' );
        if ( !el ) return;

        const content = this.stats.content || {};
        // Reversed: ECharts draws a category axis bottom-up, so the sidebar's
        // first kind would otherwise land at the bottom of the chart.
        const types = CONTENT_TYPES.slice().reverse();

        const series = STATUS_SERIES.map( ( status ) => ( {
            name: _lcs( status.label ),
            type: "bar",
            stack: "total",
            barMaxWidth: 26,
            itemStyle: { color: status.color, borderRadius: 0 },
            emphasis: { focus: "series" },
            data: types.map( ( type ) => this._byStatus( content[ type.key ] )[ status.key ] || 0 )
        } ) );

        // Whatever the three charted buckets do not cover (cancelled,
        // postponed, an unknown status) still has to add up to the total the
        // KPI shows — an unlabelled remainder is how a dashboard starts lying.
        const other = types.map( ( type ) => {
            const stats = content[ type.key ];
            const byStatus = this._byStatus( stats );
            const charted = STATUS_SERIES.reduce( ( sum, s ) => sum + ( byStatus[ s.key ] || 0 ), 0 );
            return Math.max( this._total( stats ) - charted, 0 );
        } );

        if ( other.some( ( n ) => n > 0 ) )
        {
            series.push( {
                name: _lcs( "Sonstige" ),
                type: "bar",
                stack: "total",
                barMaxWidth: 26,
                itemStyle: { color: "#DAE0E2" },  /* slate-200 */
                emphasis: { focus: "series" },
                data: other
            } );
        }

        const chart = echarts.init( el );
        chart.setOption( {
            textStyle: { fontFamily: "Inter, sans-serif", color: "#586A72" },
            grid: { left: 4, right: 16, top: 8, bottom: 32, containLabel: true },
            legend: {
                bottom: 0,
                itemWidth: 10,
                itemHeight: 10,
                icon: "circle",
                textStyle: { fontSize: 12, color: "#586A72" }
            },
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                backgroundColor: "#15332B",
                borderWidth: 0,
                textStyle: { color: "#F2E9DC", fontSize: 12 }
            },
            xAxis: {
                type: "value",
                minInterval: 1,
                splitLine: { lineStyle: { color: "rgba(31, 75, 63, 0.10)" } },
                axisLabel: { fontSize: 11, color: "#74858C" }
            },
            yAxis: {
                type: "category",
                data: types.map( ( type ) => _lcs( type.label ) ),
                axisTick: { show: false },
                axisLine: { lineStyle: { color: "rgba(31, 75, 63, 0.18)" } },
                axisLabel: { fontSize: 12, color: "#41525A" }
            },
            series: series
        } );

        this.charts.push( chart );
    }

    /** Share of the pool per content kind. */
    renderMixChart()
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="chart-mix"]' );
        if ( !el ) return;

        const content = this.stats.content || {};
        const data = CONTENT_TYPES.map( ( type, i ) => ( {
            name: _lcs( type.label ),
            value: this._total( content[ type.key ] ),
            itemStyle: { color: MIX_COLORS[ i % MIX_COLORS.length ] }
        } ) );

        const total = data.reduce( ( sum, slice ) => sum + slice.value, 0 );

        const chart = echarts.init( el );
        chart.setOption( {
            textStyle: { fontFamily: "Inter, sans-serif", color: "#586A72" },
            tooltip: {
                trigger: "item",
                backgroundColor: "#15332B",
                borderWidth: 0,
                textStyle: { color: "#F2E9DC", fontSize: 12 },
                formatter: "{b}: {c} ({d} %)"
            },
            legend: {
                bottom: 0,
                itemWidth: 10,
                itemHeight: 10,
                icon: "circle",
                textStyle: { fontSize: 12, color: "#586A72" }
            },
            series: [ {
                type: "pie",
                radius: [ "52%", "76%" ],
                center: [ "50%", "42%" ],
                avoidLabelOverlap: true,
                itemStyle: { borderColor: "#FFFFFF", borderWidth: 2 },
                label: {
                    show: true,
                    position: "center",
                    formatter: () => this._n( total ) + "\n" + _lcs( "Inhalte" ),
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#1F4B3F",
                    lineHeight: 20
                },
                // The centre label is the total, not the hovered slice — the
                // tooltip already says what a slice is.
                emphasis: { label: { show: true } },
                labelLine: { show: false },
                data: data
            } ]
        } );

        this.charts.push( chart );
    }

    /* ------------------------------- Actions -------------------------------- */

    onAction( e )
    {
        const routed = e.target.closest( "[data-route]" );
        if ( routed )
        {
            e.preventDefault();
            this.app.router.redirect( routed.getAttribute( "data-route" ) );
        }
    }

    /* -------------------------------- Helpers ------------------------------- */

    showMessage( icon, title, text )
    {
        this.disposeCharts();
        this.bodyEl.hidden = true;
        this.msgEl.hidden = false;

        const iconEl = this.msgEl.querySelector( '[data-id="msg-icon"]' );
        if ( iconEl ) iconEl.className = "icon icon--" + icon;
        this._text( "msg-title", title );
        this._text( "msg-text", text );
    }

    showBody()
    {
        this.msgEl.hidden = true;
        this.bodyEl.hidden = false;
    }

    _text( id, value )
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    /**
     * A KPI's second line: text, tone class and icon in one call, because the
     * three always change together.
     */
    _sub( id, value, toneClass = "kpi__delta--flat", icon = null )
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="' + id + '"]' );
        if ( !el ) return;

        el.className = "kpi__delta " + toneClass;
        if ( icon )
        {
            const iconEl = el.querySelector( "i" );
            if ( iconEl ) iconEl.className = "icon icon--" + icon;
        }

        const textEl = el.querySelector( "span" );
        if ( textEl ) textEl.textContent = value;
    }

    _total( stats )
    {
        return stats && typeof stats.total === "number" ? stats.total : 0;
    }

    _byStatus( stats )
    {
        return ( stats && stats.byStatus ) || {};
    }

    _n( value )
    {
        return Number( value || 0 ).toLocaleString( locale() );
    }

    /**
     * AI calls cost fractions of a cent, so a plain two-decimal format would
     * show a month of real usage as "0,00 $". Below one cent the amount is
     * shown as "< 0,01 $" instead — honest without a wall of decimals.
     */
    _money( value, currency = "USD" )
    {
        const amount = Number( value || 0 );
        const format = ( n ) => n.toLocaleString( locale(), {
            style: "currency", currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2
        } );

        if ( amount > 0 && amount < 0.01 ) return "< " + format( 0.01 );
        return format( amount );
    }

    /** "2026-09" → "September 2026" in the console language. */
    _monthText( month )
    {
        const date = new Date( month + "-01T00:00:00Z" );
        if ( isNaN( date.getTime() ) ) return month;
        return date.toLocaleString( locale(), { month: "long", year: "numeric", timeZone: "UTC" } );
    }

    _dateText( value )
    {
        if ( !value ) return _lcs( "noch kein Import" );

        const date = new Date( value );
        if ( isNaN( date.getTime() ) ) return "–";

        return _lcs( "{time} Uhr", {
            time : date.toLocaleString( locale(), {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
            } )
        } );
    }

    _esc( value )
    {
        return String( value === null || value === undefined ? "" : value )
            .replace( /&/g, "&amp;" )
            .replace( /</g, "&lt;" )
            .replace( />/g, "&gt;" )
            .replace( /"/g, "&quot;" );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( 'Übersicht' ),
            subtitle: _lcs( 'Plattform-Status auf einen Blick' )
        };
    }
}
